import { Component, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatChipInput,
  MatChipInputEvent,
  MatChipsModule,
} from '@angular/material/chips';
import {
  MatAutocomplete,
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgFor, NgIf } from '@angular/common';
import { pKeyword } from '../../interfaces/pKeyword';
import { MatInputModule } from '@angular/material/input';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { pTicket } from '../../interfaces/pTicket';
import { AsyncPipe } from '@angular/common';
import { PKeywordService } from '../../services/p-keyword.service';
import { masterpKeyword } from '../../interfaces/masterData';

@Component({
  selector: 'app-p-keyword-picker',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatChipsModule,
    MatInputModule,
    MatChipInput,
    MatAutocompleteModule,
    ReactiveFormsModule,
    NgFor,
    NgIf,
    MatIconModule,
    AsyncPipe,
  ],
  templateUrl: './p-keyword-picker.component.html',
  styleUrl: './p-keyword-picker.component.scss',
})
export class PKeywordPickerComponent implements OnInit, OnChanges {
  @Input() canUpdate: boolean = false;
  @Input() pTicket!: pTicket;

  keywordCtrl = new FormControl('');
  filteredKeywords: Observable<masterpKeyword[]>;
  pKeywords: pKeyword[] = [];
  masterpKeywords!: masterpKeyword[];
  separatorKeysCodes: number[] = [ENTER, COMMA];

  constructor(private pKeywordService: PKeywordService) {
    this.filteredKeywords = this.keywordCtrl.valueChanges.pipe(
      startWith(null),
      map((value: string | null) => this._filter(value)),
    );
  }

  ngOnInit(): void {
    this.getPKeywordsList();
    this.getSelectedKeywords();
    this.refreshDisabledState();
  }

  ngOnChanges(): void {
    if (!this.pTicket) return;
    this.refreshDisabledState();
  }

  private refreshDisabledState(): void {
    const shouldDisable =
      !this.canUpdate || this.pTicket?.pct_status === 'closed';
    shouldDisable ? this.keywordCtrl.enable() : this.keywordCtrl.disable();
  }

  addKeywordToTicket(selectedValue: any): void {
    if (this.keywordCtrl.disabled) return;

    if (selectedValue) {
      const matchingKeyword = this.masterpKeywords.find(
        (k) => k.mpk_value === selectedValue,
      );
      if (matchingKeyword) {
        this.pKeywordService
          .create({
            pticket_id: this.pTicket.id,
            pkw_value: matchingKeyword.mpk_value,
          })
          .subscribe(() => this.getSelectedKeywords());
      }
    }
    this.keywordCtrl.setValue(null);
  }

  @ViewChild('auto') autoComplete!: MatAutocomplete;

  get autocompleteRef(): MatAutocomplete | null {
    return this.keywordCtrl.disabled ? null : this.autoComplete;
  }

  addKeyword(event: MatChipInputEvent): void {
    if (this.keywordCtrl.disabled) return;
    this.addKeywordToTicket(event.value);
    event.input!.value = '';
  }

  selectKeyword(event: MatAutocompleteSelectedEvent): void {
    if (this.keywordCtrl.disabled) return;
    this.addKeywordToTicket(event.option.value);
  }

  removeKeyword(keyword: pKeyword): void {
    if (this.keywordCtrl.disabled) return;

    const index = this.pKeywords.indexOf(keyword);
    if (index >= 0) {
      this.pKeywords.splice(index, 1);
      this.pKeywordService
        .deleteByID(keyword.id)
        .subscribe(() => this.getSelectedKeywords());
    }
  }

  private _filter(value: string | null): masterpKeyword[] {
    if (!this.masterpKeywords) return [];
    if (!value) return this.masterpKeywords.slice();
    const filterValue = value.toLowerCase();
    return this.masterpKeywords.filter((k) =>
      k.mpk_value.toLowerCase().includes(filterValue),
    );
  }

  getPKeywordsList(): void {
    this.pKeywordService.getDefaultList().subscribe((defaultKeywords) => {
      this.masterpKeywords = defaultKeywords;
    });
  }

  getSelectedKeywords(): void {
    this.pKeywordService
      .getByTicketID(this.pTicket.id)
      .subscribe((keywords) => {
        this.pKeywords = keywords;
      });
  }
}
